import log from "loglevel";
import { apiCoreEndpoint } from "settings";
import { verifyJwtService } from "store/resources/jwt/service";
import * as t from "./types";
import { processServiceResponse } from "lib/request";

export const loginUserService = (request: t.LoginUserRequestType) => {
  const LOGIN_API_ENDPOINT = `${apiCoreEndpoint}/auth/login`;
  log.info("Login Service Request: ", request);
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
    body: JSON.stringify(request.body),
  };

  return fetch(LOGIN_API_ENDPOINT, parameters)
    .then((response) => {
      if (response.ok) {
        log.info("Successful Login!");
        localStorage.clear(); // Clear all local storage
        sessionStorage.clear(); // Clear all session storage
        return response.json();
      } else if (response.status === 401) {
        log.warn("BAD Login!");
        return {
          error: {
            message: "Invalid Username/Password",
          },
        };
      } else {
        log.warn("Server Error while logging in.");
        return {
          error: {
            message: "Server Error. Please try again later!",
          },
        };
      }
    })
    .then((json) => {
      return json;
    })
    .catch((error) => {
      log.error(`Login attempt failed! Reason: ${error}`);
      return {
        error: {
          message: "Unable to process request. Please try again later.",
        },
      };
    });
};

export const logoutUserService = (request?: t.LogoutUserRequestType) => {
  const LOGOUT_API_ENDPOINT = `${apiCoreEndpoint}/auth/logout`;
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
  };

  return fetch(LOGOUT_API_ENDPOINT, parameters)
    .then((response) => {
      if (response.ok) {
        log.info("Successful Logout!");
        localStorage.clear(); // Clear all local storage
        sessionStorage.clear(); // Clear all session storage
      } else {
        log.info("Could not successfully logout!");
        localStorage.clear(); // Clear all local storage
        sessionStorage.clear(); // Clear all session storage
      }
      return response;
    })
    .catch((error) => {
      log.error(`Fetch failed! Reason: ${error}`);
      localStorage.clear(); // Clear all local storage
      sessionStorage.clear(); // Clear all session storage
      return {
        error: {
          message: "Request failed",
        },
      };
    });
};

export const twoFactorStatusService = async () => {
  log.info("Checking if User is Two-factor Authenticated...");
  const TWOFACTOR_STATUS_API_ENDPOINT = `${apiCoreEndpoint}/auth/decode/two-factor/status`;
  const parameters = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
  };

  const response = await fetch(TWOFACTOR_STATUS_API_ENDPOINT, parameters);
  let json;
  if (response.ok) {
    json = await response.json();
  } else if (response.status === 401) {
    json = {
      error: {
        success: false,
        message: "Unauthorized",
      },
    };
  } else {
    json = {
      error: {
        success: false,
        message: "Unable to process request",
      },
    };
  }
  return json;
};

// Authy
export const registerAuthyUserService = (
  request: t.AddAuthyUserRequestType
) => {
  const REGISTER_AUTHY_USER_API_ENDPOINT = `${apiCoreEndpoint}/auth/two-factor/authy/current-user/`;
  log.info("Create new Authy User Request: ", request);
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
    body: JSON.stringify(request.body),
  };

  return fetch(REGISTER_AUTHY_USER_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, REGISTER_AUTHY_USER_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    })
    .catch((error) => {
      log.error(`Fetch failed! Reason: ${error}`);
      return {
        error: {
          message: "Request failed",
        },
      };
    });
};

export const textAuthyOtpService = (request?: t.TextAuthyOtpRequestType) => {
  const TEXT_AUTHY_OTP_API_ENDPOINT = `${apiCoreEndpoint}/auth/two-factor/authy/current-user/otp/sms`;
  const parameters = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
  };

  return fetch(TEXT_AUTHY_OTP_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, TEXT_AUTHY_OTP_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    });
};

export const callAuthyOtpService = (request?: t.CallAuthyOtpRequestType) => {
  const CALL_AUTHY_OTP_API_ENDPOINT = `${apiCoreEndpoint}/auth/two-factor/authy/current-user/otp/voice`;
  const parameters = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
  };

  return fetch(CALL_AUTHY_OTP_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, CALL_AUTHY_OTP_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    });
};

export const verifyAuthyOtpService = (request: t.VerifyAuthyOtpRequestType) => {
  const VERIFY_AUTHY_OTP_API_ENDPOINT = `${apiCoreEndpoint}/auth/two-factor/authy/current-user/verify/`;
  log.info("Verify Authy OTP Request: ", request);
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
    body: JSON.stringify(request.body),
  };

  return fetch(VERIFY_AUTHY_OTP_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, VERIFY_AUTHY_OTP_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    })
    .catch((error) => {
      log.error(`Fetch failed! Reason: ${error}`);
      return {
        error: {
          message: "Request failed",
        },
      };
    });
};

// EMAIL
export const verifyEmailService = async (token: string) => {
  log.info("Verifying verification token provided by user...");
  const VERIFY_EMAIL_API_ENDPOINT = `${apiCoreEndpoint}/auth/email/token?value=${token}`;
  const parameters = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  const response = await fetch(VERIFY_EMAIL_API_ENDPOINT, parameters);
  const json = await response.json();
  return json;
};

export const createPasswordNoAuthService = (
  request: t.CreatePasswordNoAuthRequestType
) => {
  log.info("Setting user's password...");
  const CREATE_PASSWORD_NO_AUTH_API_ENDPOINT = `${apiCoreEndpoint}/auth/email/change-password`;
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request.body),
  };

  return fetch(CREATE_PASSWORD_NO_AUTH_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(
        response,
        CREATE_PASSWORD_NO_AUTH_API_ENDPOINT
      );
    })
    .then((json) => {
      return json;
    });
};

export const resetPhoneService = (request: t.ResetPhoneRequestType) => {
  log.info("Deactivating user's current phone...");
  const RESET_OP_BASIC_AUTH_API_ENDPOINT = `${apiCoreEndpoint}/auth/email/deactivate-phone`;
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
    body: JSON.stringify(request.body),
  };

  return fetch(RESET_OP_BASIC_AUTH_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, RESET_OP_BASIC_AUTH_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    });
};

export const resetOperationEmailNoAuthService = (
  request: t.ResetOperationEmailNoAuthRequestType
) => {
  log.info("Sending email allowing user to reset password and/or verify...");
  const RESET_OP_NO_AUTH_API_ENDPOINT = `${apiCoreEndpoint}/auth/email/send-verify-reset`;
  const parameters = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request.body),
  };

  return fetch(RESET_OP_NO_AUTH_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(response, RESET_OP_NO_AUTH_API_ENDPOINT);
    })
    .then((json) => {
      return json;
    });
};

export const isBasicVerified = async () => {
  log.info("Checking if user is logged in...");
  const jwtInLocalStorage = localStorage.getItem("user_session");
  if (jwtInLocalStorage) {
    const userIsLoggedIn = await verifyJwtService();
    if (userIsLoggedIn.data) {
      log.info("User is logged in.");
      return true;
    }
  }
  log.info("User is NOT logged in!");
  return false;
};

export const isTwoFactorVerified = async () => {
  log.info("Checking if user is logged in...");
  const jwtInLocalStorage = localStorage.getItem("user_session");
  if (jwtInLocalStorage) {
    const userIsLoggedIn = await verifyJwtService();
    const userIsTwoFactored = await twoFactorStatusService();
    if (userIsLoggedIn.data) {
      log.info("User is logged in. Checking Two-factor status...");
      if (userIsTwoFactored.data) {
        const twofactored_status = userIsTwoFactored.data.twofactor_status;
        if (twofactored_status) {
          log.info("User is Two-factor Authenticated!");
          return true;
        } else {
          log.info("User is logged in, but NOT Two-Factored!");
        }
      }
    }
  }
  log.info("User is NOT Verified!");
  return false;
};

export const getCurrentUserAuthyStatus = (
  request?: t.GetCurrentUserAuthyStatusRequestType
) => {
  const GET_USER_AUTHY_STATUS_API_ENDPOINT = `${apiCoreEndpoint}/auth/two-factor/authy/current-user/`;
  const parameters = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_session")}`,
    },
  };

  return fetch(GET_USER_AUTHY_STATUS_API_ENDPOINT, parameters)
    .then((response) => {
      return processServiceResponse(
        response,
        GET_USER_AUTHY_STATUS_API_ENDPOINT
      );
    })
    .then((json) => {
      return json;
    });
};

export const isTwoFactorRegistered = async () => {
  log.info("Checking if user is already two-factor registered in...");
  const jwtInLocalStorage = localStorage.getItem("user_session");
  if (jwtInLocalStorage) {
    const isUserRegistered = await getCurrentUserAuthyStatus();
    if (isUserRegistered.data) {
      log.info("Got back an OK response. Checking status...");
      if (isUserRegistered.data.success) {
        return true;
      }
    }
  }
  log.info("User is NOT two-factor registered!");
  return false;
};

export const isEmailVerifying = async (
  verificationToken: string | undefined
) => {
  log.info("Checking if user clicked email verfication link...");
  if (verificationToken) {
    const userIsVerifyingEmail = await verifyEmailService(verificationToken);
    log.info("userIsVerifyingEmail: " + JSON.stringify(userIsVerifyingEmail));
    if (userIsVerifyingEmail.data) {
      log.info("Valid token provided. Allow user to set password...");
      return true;
    } else if (userIsVerifyingEmail.error) {
      log.warn("Prohibited");
    } else {
      log.warn("BAD response from API");
    }
  }
  return false;
};

export const isEmailVerifyingBasicAuth = async (
  verificationToken: string | undefined
) => {
  log.info("Checking if user clicked email verfication link...");
  const isEmailVerified = await isEmailVerifying(verificationToken);
  log.info("isEmailVerified: " + isEmailVerified);
  if (isEmailVerified) {
    log.info("Valid token provided. Checking Basic Auth...");
    const jwtInLocalStorage = localStorage.getItem("user_session");
    if (jwtInLocalStorage) {
      const userIsLoggedIn = await verifyJwtService();
      if (userIsLoggedIn.data) {
        log.info("User is logged in. Allow to continue...");
        return true;
      } else {
        log.info("User is NOT logged in!");
      }
    }
  } else {
    log.info("Email Verification Token is not valid!");
  }
  return false;
};
